// Schedule custom panel for the unified settings plane. Wraps the existing
// GET/POST /api/v1/settings/schedule endpoint (which predates the common
// Store abstraction) in a single-row form that fits visually with FormPanel.
//
// Styling matches FormPanel's card conventions so the tab switching stays
// visually uniform.

import { type FormEvent } from 'react'
import { Button, Card, FormRow, Input, Toggle } from '@/admin-design/_common'
import { useCustomPanelForm } from '@/admin-design/_common/settings'
import type { Group } from '@/admin-design/_common/settings/types'
import { api } from '@/api/client'

interface Props {
  group: Group
  endpoint: string
  onDirtyChange?: (dirty: boolean) => void
}

const DEFAULT_FORM = {
  crawl_cron: '0 */6 * * *',
  register_cron: '',
  naver_pull_cron: '',
  supplier_soldout_cron: '',
  product_state_cron: '0 4 * * *',
  expose_floor_cron: '0 7 * * *',
  expose_floor_dry_run: false,
  expose_floor_max_raise_rate: 2.0,
  margin_floor_target_rate: 0.2,
  margin_floor_supplier_ship: 3500,
  margin_floor_collected_ship: 3000,
  margin_floor_fee_rate: 0.058,
  price_change_pct: 30,
  margin_rate: 1.5,
  enabled: true,
}

type ScheduleData = typeof DEFAULT_FORM

export default function SchedulePanel({ group, endpoint, onDirtyChange }: Props) {
  const form = useCustomPanelForm<ScheduleData, ScheduleData>({
    endpoint,
    toDraft: (s) => ({ ...DEFAULT_FORM, ...s }),
    save: (draft) => api.saveSchedule(draft),
    onDirtyChange,
  })

  const draft = form.draft
  const setForm = form.setDraft
  const dirty = form.dirty

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    await form.save()
  }

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <Card.Title>
          {group.title}
          {dirty && <span className="text-xs font-normal text-warning ml-2">● unsaved</span>}
        </Card.Title>
        {group.description && <Card.Description>{group.description}</Card.Description>}
      </Card.Header>

      <form onSubmit={onSubmit}>
        <div className="divide-y divide-border">
          <div className="px-6 py-4">
            <FormRow label="크롤 주기 (cron)" help="예: 0 */6 * * * (6시간마다)">
              <Input
                type="text"
                className="max-w-md font-mono"
                value={draft.crawl_cron}
                onChange={(e) => setForm({ ...draft, crawl_cron: e.target.value })}
              />
            </FormRow>
          </div>

          <div className="px-6 py-4">
            <FormRow
              label="자동 등록 주기 (cron)"
              help="비우면 자동 등록 비활성화 — 크롤 후 수동 등록"
            >
              <Input
                type="text"
                className="max-w-md font-mono"
                value={draft.register_cron}
                onChange={(e) => setForm({ ...draft, register_cron: e.target.value })}
              />
            </FormRow>
          </div>

          <div className="px-6 py-4">
            <FormRow
              label="상품 상태 동기화 주기 (cron)"
              help="네이버→DB, 도매처→DB, DB→네이버를 순서대로 1회 실행. 예: 0 4 * * * (매일 새벽 4시)"
            >
              <Input
                type="text"
                className="max-w-md font-mono"
                value={draft.product_state_cron}
                onChange={(e) => setForm({ ...draft, product_state_cron: e.target.value })}
              />
            </FormRow>
          </div>

          <div className="px-6 py-4">
            <FormRow
              label="노출최저가 강제 주기 (cron)"
              help="도매처 노출최저가보다 싸게 걸린 상품을 노출최저가로 끌어올림. 크롤(06시) 이후여야 의미가 있음. 비우면 사용 안 함"
            >
              <Input
                type="text"
                className="max-w-md font-mono"
                value={draft.expose_floor_cron}
                onChange={(e) => setForm({ ...draft, expose_floor_cron: e.target.value })}
              />
            </FormRow>
          </div>

          <div className="px-6 py-4">
            <FormRow
              label="노출최저가 인상 상한 (배)"
              help="현재가의 몇 배까지 자동 인상할지. 초과분은 적용하지 않고 경고 + 알림만 남김 (추천: 2.0)"
            >
              <Input
                type="number"
                className="w-32"
                value={draft.expose_floor_max_raise_rate}
                step="0.1"
                min="1.0"
                max="10.0"
                onChange={(e) =>
                  setForm({ ...draft, expose_floor_max_raise_rate: parseFloat(e.target.value) || 2.0 })
                }
              />
            </FormRow>
          </div>

          <div className="px-6 py-4">
            <FormRow
              label="목표 실질 마진율"
              help="배송비 역전과 네이버 수수료를 뺀 '실질' 마진이 결제금액 대비 이 비율 이상이 되도록 판매가를 올림 (0.20 = 20%). 0이면 마진 바닥을 끔"
            >
              <Input
                type="number"
                className="w-32"
                value={draft.margin_floor_target_rate}
                step="0.01"
                min="0"
                max="0.9"
                onChange={(e) =>
                  setForm({ ...draft, margin_floor_target_rate: parseFloat(e.target.value) || 0 })
                }
              />
            </FormRow>
          </div>

          <div className="px-6 py-4">
            <FormRow
              label="도매처 배송비 (원)"
              help="주문 1건당 도매처에 내는 배송비. 네이버에서 받는 배송비보다 크면 그 차액만큼 주문마다 손해가 깔린다"
            >
              <Input
                type="number"
                className="w-32"
                value={draft.margin_floor_supplier_ship}
                step="100"
                min="0"
                onChange={(e) =>
                  setForm({ ...draft, margin_floor_supplier_ship: parseInt(e.target.value, 10) || 0 })
                }
              />
            </FormRow>
          </div>

          <div className="px-6 py-4">
            <FormRow label="수취 배송비 (원)" help="네이버에서 고객에게 받는 배송비">
              <Input
                type="number"
                className="w-32"
                value={draft.margin_floor_collected_ship}
                step="100"
                min="0"
                onChange={(e) =>
                  setForm({ ...draft, margin_floor_collected_ship: parseInt(e.target.value, 10) || 0 })
                }
              />
            </FormRow>
          </div>

          <div className="px-6 py-4">
            <FormRow
              label="네이버 수수료율"
              help="주문관리 + 매출연동. 결제금액(판매가+수취배송비) 기준 (0.058 = 5.8%). 카테고리마다 다르므로 정산 내역으로 확인할 것"
            >
              <Input
                type="number"
                className="w-32"
                value={draft.margin_floor_fee_rate}
                step="0.001"
                min="0"
                max="0.5"
                onChange={(e) =>
                  setForm({ ...draft, margin_floor_fee_rate: parseFloat(e.target.value) || 0 })
                }
              />
            </FormRow>
          </div>

          <div className="px-6 py-4">
            <FormRow
              label="노출최저가 강제 — 계산만"
              help="켜면 네이버에 반영하지 않고 로그로만 남김"
            >
              <Toggle
                checked={draft.expose_floor_dry_run}
                onChange={(v) => setForm({ ...draft, expose_floor_dry_run: v })}
              />
            </FormRow>
          </div>

          <div className="px-6 py-4">
            <FormRow
              label="네이버 상태/가격 동기화 주기 (cron)"
              help="위 통합 동기화의 1단계만 따로 돌릴 때만 입력 — 비우면 사용 안 함"
            >
              <Input
                type="text"
                className="max-w-md font-mono"
                value={draft.naver_pull_cron}
                onChange={(e) => setForm({ ...draft, naver_pull_cron: e.target.value })}
              />
            </FormRow>
          </div>

          <div className="px-6 py-4">
            <FormRow
              label="도매처 품절 반영 주기 (cron)"
              help="품절만 반영하던 구 방식 — 비우면 사용 안 함"
            >
              <Input
                type="text"
                className="max-w-md font-mono"
                value={draft.supplier_soldout_cron}
                onChange={(e) => setForm({ ...draft, supplier_soldout_cron: e.target.value })}
              />
            </FormRow>
          </div>

          <div className="px-6 py-4">
            <FormRow
              label={
                <>
                  가격 변동 허용 <span className="text-xs font-normal text-muted-foreground">(%)</span>
                </>
              }
              help="도매처 가격이 이 % 이상 변하면 자동 반영하지 않고 로그로만 남김 (추천: 30%)"
            >
              <Input
                type="number"
                className="w-32"
                value={draft.price_change_pct}
                step="0.1"
                min="0"
                max="100"
                onChange={(e) => setForm({ ...draft, price_change_pct: parseFloat(e.target.value) })}
              />
            </FormRow>
          </div>

          <div className="px-6 py-4">
            <FormRow
              label="마진율"
              help="노출최저가가 없을 때 도매가에 적용할 배율 (1.5 = +50%). register 실행 시 적용"
            >
              <Input
                type="number"
                className="w-32"
                value={draft.margin_rate}
                step="0.05"
                min="1.0"
                max="3.0"
                onChange={(e) => setForm({ ...draft, margin_rate: parseFloat(e.target.value) || 1.5 })}
              />
            </FormRow>
          </div>

          <div className="px-6 py-4">
            <FormRow label="스케줄 활성화">
              <Toggle
                checked={draft.enabled}
                onChange={(v) => setForm({ ...draft, enabled: v })}
              />
            </FormRow>
          </div>
        </div>

        <Card.Footer className="flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">
            {form.error && <span className="text-destructive">저장 실패</span>}
            {dirty && '변경사항이 저장되지 않았습니다'}
          </div>
          <Button type="submit" size="sm" variant="primary" loading={form.isSaving} disabled={!dirty || form.isSaving}>
            {form.isSaving ? 'Saving…' : 'Save changes'}
          </Button>
        </Card.Footer>
      </form>
    </Card>
  )
}
